import { fetchWithJwt } from '../../utils/fetchWithJwt';
import { PimsSchema, PimsConfig, PimsStatus, ImportState } from '../../types/pims';

interface PatientSearchResult {
  id: string;
  name?: string;
  parentName?: string;
  updatedAt?: string;
}

export async function searchPatients(query: string) {
  const patientFetch = await fetchWithJwt(`/patients/search?n=${query}`);
  const patients = await patientFetch.json() as PatientSearchResult[];
  if (patients.length === 0) {
    return [];
  }
  return patients.map((patient) => ({
    id: patient.id,
    animalName: patient.name,
    parentName: patient.parentName || '',
    updatedAt: patient.updatedAt || ''
  }));
}

export async function createSession(data: { patientId: string; deviceId: string }) {
  await fetchWithJwt("/sessions", {
    method: "POST",
    body: JSON.stringify(data)
  });
}

interface CreatePatientData {
  animalName: string;
  species: string;
  breed: string;
  sex: string;
  ownerFirstName: string;
  ownerLastName: string;
  address?: string;
  phone?: string;
}

interface PatientPayload {
  name: string;
  species: string;
  breed: string;
  sex: string;
  parentName: string;
  address?: string;
  phone?: string;
}

export async function createPatient(data: CreatePatientData) {
  const payload: PatientPayload = {
    name: data.animalName,
    species: data.species.toLowerCase(),
    breed: data.breed.toLowerCase(),
    sex: data.sex.toLowerCase(),
    parentName: data.ownerFirstName + " " + data.ownerLastName,
    ...(data.address && { address: data.address }),
    ...(data.phone && { phone: data.phone }),
  };
  const sdata = await fetchWithJwt("/patients", {
    method: "POST",
    body: payload
  });
  return await sdata.json();
}

export async function searchBreeds(query: string) {
  return ["Labrador", "Golden Retriever", "Persian", "Maine Coon"].filter((b) =>
    b.toLowerCase().includes(query.toLowerCase())
  );
}

export async function createDevice(data: {
  locationId: string;
  locationCode: string;
  locationColor: string;
}) {
  const response = await fetchWithJwt("/devices", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return await response.json();
}

export async function updateDevice(id: string, data: {
  locationId: string;
  locationCode: string;
  locationColor: string;
}) {
  const response = await fetchWithJwt(`/devices/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return await response.json();
}

export async function removeSession(sessionId: string, deviceId: string, patientId: string) {
  await fetchWithJwt(`/sessions/${sessionId}`, {
    method: "DELETE",
    body: JSON.stringify({ patientId, deviceId }),
  });
  return { success: true };
}

// ---------- PIMS Integration API ----------

export const fetchSupportedPims = async (): Promise<PimsStatus[]> => {
  const res = await fetchWithJwt('/pims-configuration');
  const configs = await res.json();

  interface PimsConfigResponse {
    id: string;
    integrationType: string;
  }
  
  return ['Covetrus', 'Shepherd', 'Vetspire', 'Digitail'].map(name => {
    const matched = configs.find((p: PimsConfigResponse) => p.integrationType === name);
    return {
      id: matched ? matched.id : name.toLowerCase(),
      name,
      isEnabled: !!matched,
      pimsType: name.toLowerCase(),
    };
  });
};

export const fetchPimsFields = async (pimsId: string): Promise<PimsSchema> => {
  const res = await fetchWithJwt(`/pims-configuration/schema/${pimsId}`);
  const fieldsObj = await res.json();

  // Transform the object into an array of PimsField objects
  interface FieldValue {
    required?: boolean;
  }
  const fields = Object.entries(fieldsObj as Record<string, FieldValue>).map(([key, value]) => ({
    key,
    label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'), // Convert camelCase to Title Case
    type: 'password' as const, // Explicitly type as 'password'
    required: value.required || false
  }));

  return {
    id: pimsId,
    name: pimsId.charAt(0).toUpperCase() + pimsId.slice(1),
    fields,
  };
};

export const fetchPimsConfig = async (pimsId: string): Promise<PimsConfig | null> => {
  const res = await fetchWithJwt(`/pims-configuration/${pimsId}`);
  if (res.status === 404) return null;
  const data = await res.json();
  return data.config || null;
};

export const savePimsConfig = async (pimsId: string, config: PimsConfig): Promise<void> => {
  const existingRes = await fetchWithJwt(`/pims-configuration/${pimsId}`);
  if (existingRes.ok) {
    const existing = await existingRes.json();
    await fetchWithJwt(`/pims-configuration/${existing.id}`, {
      method: "PUT",
      body: JSON.stringify({
        config,
        pimsEnabled: true,
      }),
    });
  } else {
    await fetchWithJwt('/pims-configuration', {
      method: "POST",
      body: JSON.stringify({
        integrationType: pimsId.charAt(0).toUpperCase() + pimsId.slice(1),
        config,
        pimsEnabled: true,
      }),
    });
  }
};

export async function saveManualVitals(data: {
  heartRate?: number;
  respiratoryRate?: number;
  temperature?: number;
  weight?: number;
  timestamp: string;
  deviceId: string;
  sessionId?: string;
  patientId?: string | number;
}) {
  const response = await fetchWithJwt("/manual-vitals", {
    method: "POST",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to save manual vitals: ${errorText}`);
  }

  return await response.json();
}

export type { ImportState };

export async function startPimsImport(pimsId: string): Promise<void> {
  await fetchWithJwt(`/pims-configuration/${pimsId}/import`, {
    method: "POST"
  });
}

export async function stopPimsImport(pimsId: string): Promise<void> {
  await fetchWithJwt(`/pims-configuration/${pimsId}/import`, {
    method: "DELETE"
  });
}

export async function getImportState(pimsId: string): Promise<ImportState> {
  const res = await fetchWithJwt(`/pims-configuration/${pimsId}/import/state`);
  return await res.json();
}